// Per-slot editor for the campaign-setup wizard. Owns its own character
// draft state via useCharacterDraft so the wizard slot and the /characters
// editor consume the same hook for the same purpose.
//
// Why this exists: the parent CampaignSetup used to maintain a giant
// `characterSlots[]` array containing both slot metadata (slot_id, seat_id,
// was_pregenerated, etc.) AND character draft fields (name, portrait_url,
// stage_cutout_url, etc.). Every slot mutation went through array-map
// rewrites in the parent. Three BOU-926 iterations slipped through because
// each mutation path hand-copied a different subset of fields. This component
// pulls the per-slot state into one place — the useCharacterDraft hook —
// and exposes an imperative ref API for the parent's aggregate operations
// (autoFillAll, finalize, validation).
//
// Architecture:
//   - Each slot is a <CampaignSlotEditor ref={...}> instance.
//   - The hook owns the draft. The editor owns selection + meta-modification
//     tracking + per-slot async UI state (generating backstory / cutout).
//   - The editor reports its full snapshot upward via `onSnapshot(slotId, snap)`
//     after every mutation, so the parent has a cached read-only view.
//   - The parent uses the cached snapshots for read-only aggregate ops
//     (validation, other-slot selection filtering, finalize payload build).
//   - The parent uses ref.current.hydrateFromSource(...) for aggregate write
//     ops (autoFillAllCharacters).

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useCharacterDraft } from '../hooks/useCharacterDraft.js';
import { usePortraitContext } from '../contexts/PortraitContext.jsx';
import ThumbnailImg from './common/ThumbnailImg.jsx';

const CampaignSlotEditor = forwardRef(({
  slotId,
  seatIndex,
  seatId,
  pregeneratedCharacters,
  myCharacters,
  // Names/IDs already selected in OTHER slots — used to filter dropdowns so
  // two slots can't pick the same character.
  otherPregenNames,
  otherMyCharIds,
  generatingBackstory: parentGeneratingBackstory,
  // Called whenever any internal state changes so the parent can keep a cached
  // snapshot for aggregate operations. Signature: (slotId, snapshot).
  onSnapshot,
  // Fired synchronously when the user clicks a tile, before the snapshot
  // round-trip lands. Lets the parent flip to a loading screen the instant a
  // pick lands on the slot that completes the roster, instead of leaving the
  // picker grid visible during the multi-second finalize call. Signature:
  // (slotId).
  onPicked,
  onCreateCharacter,
}, ref) => {
  const {
    draft,
    setField,
    setFields,
    hydrateFromSource,
    reset,
  } = useCharacterDraft();

  const { getPortraitUrl } = usePortraitContext();

  // Slot-local metadata (lives alongside the draft, not in the parent).
  const [selection, setSelection] = useState({ type: null, value: null });
  const [, setOriginalPregenData] = useState(null);
  const [isModified, setIsModified] = useState(false);
  const [existingCharacterId, setExistingCharacterId] = useState(null);
  const [wasPregenerated, setWasPregenerated] = useState(false);

  const buildSnapshot = useCallback(() => ({
    slotId,
    seatId,
    seatIndex,
    draft,
    selection,
    isModified,
    wasPregenerated,
    existingCharacterId,
    isFilled: Boolean(draft.name && draft.name.trim() !== ''),
  }), [
    slotId, seatId, seatIndex, draft, selection,
    isModified, wasPregenerated, existingCharacterId,
  ]);

  // Notify parent on every snapshot change so it can keep an aggregate cache.
  useEffect(() => {
    onSnapshot?.(slotId, buildSnapshot());
  }, [buildSnapshot, onSnapshot, slotId]);

  // Imperative API used by the parent for aggregate write operations and
  // finalize-time reads.
  useImperativeHandle(ref, () => ({
    getSnapshot: () => buildSnapshot(),
    hydrateFromSource: (source, opts = {}) => {
      hydrateFromSource(source);
      setSelection(opts.selection ?? { type: null, value: null });
      setOriginalPregenData(opts.wasPregenerated ? { ...source } : null);
      setIsModified(false);
      setWasPregenerated(Boolean(opts.wasPregenerated));
      setExistingCharacterId(opts.existingCharacterId ?? null);
    },
    clear: () => {
      reset();
      setSelection({ type: null, value: null });
      setOriginalPregenData(null);
      setIsModified(false);
      setWasPregenerated(false);
      setExistingCharacterId(null);
    },
    setField,
    setFields,
  }), [buildSnapshot, hydrateFromSource, reset, setField, setFields]);

  // ---------------- Selection handlers ----------------

  const handlePregenSelect = useCallback((name) => {
    if (!name) {
      reset();
      setSelection({ type: null, value: null });
      setOriginalPregenData(null);
      setIsModified(false);
      setWasPregenerated(false);
      setExistingCharacterId(null);
      return;
    }
    const char = pregeneratedCharacters.find((c) => c.name === name);
    if (!char) return;
    hydrateFromSource(char);
    setSelection({ type: 'pregen', value: name });
    setOriginalPregenData({ ...char });
    setIsModified(false);
    setWasPregenerated(true);
    setExistingCharacterId(null);
  }, [pregeneratedCharacters, hydrateFromSource, reset]);

  const handleMyCharSelect = useCallback((charId) => {
    if (!charId) {
      reset();
      setSelection({ type: null, value: null });
      setExistingCharacterId(null);
      setWasPregenerated(false);
      return;
    }
    const char = myCharacters.find((c) => c.character_id === charId);
    if (!char) return;
    hydrateFromSource(char);
    setSelection({ type: 'my', value: charId });
    setOriginalPregenData(null);
    setIsModified(false);
    setWasPregenerated(false);
    setExistingCharacterId(char.character_id);
  }, [myCharacters, hydrateFromSource, reset]);


  // ---------------- Field-change wiring ----------------

  // ---------------- Portrait picker (matches /characters selection) ----------------

  const [nameFilter, setNameFilter] = useState('');

  const availableMyCharacters = useMemo(() => (
    myCharacters.filter((c) => !otherMyCharIds.has(c.character_id))
  ), [myCharacters, otherMyCharIds]);
  const availablePregens = useMemo(() => (
    pregeneratedCharacters.filter((c) => !otherPregenNames.has(c.name))
  ), [pregeneratedCharacters, otherPregenNames]);

  const filteredTiles = useMemo(() => {
    const q = nameFilter.trim().toLowerCase();
    const mine = availableMyCharacters.map((c) => ({ kind: 'my', char: c }));
    const pregens = availablePregens.map((c) => ({ kind: 'pregen', char: c }));
    const all = [...mine, ...pregens];
    if (!q) return all;
    return all.filter(({ char }) => (char.name || '').toLowerCase().includes(q));
  }, [availableMyCharacters, availablePregens, nameFilter]);

  const handleTileClick = useCallback((tile) => {
    if (tile.kind === 'my') {
      handleMyCharSelect(tile.char.character_id);
    } else {
      handlePregenSelect(tile.char.name);
    }
    onPicked?.(slotId);
  }, [handleMyCharSelect, handlePregenSelect, onPicked, slotId]);

  return (
    <div className="campaign-slot-picker">
      <input
        type="search"
        className="campaign-slot-picker__filter"
        placeholder="Filter characters by name…"
        value={nameFilter}
        onChange={(e) => setNameFilter(e.target.value)}
        aria-label="Filter characters by name"
        disabled={parentGeneratingBackstory}
      />
      <div className="campaign-slot-picker__grid">
        {filteredTiles.map((tile, tileIndex) => {
          const isPicked = (
            (tile.kind === 'my' && selection.type === 'my' && selection.value === tile.char.character_id)
            || (tile.kind === 'pregen' && selection.type === 'pregen' && selection.value === tile.char.name)
          );
          const directUrl = (
            tile.char.portrait_url
            || tile.char.portrait_path
            || tile.char.image_assets?.portrait?.url
            || null
          );
          const resolvedUrl = getPortraitUrl(
            tile.char.character_id || null,
            tile.char.name || null,
            directUrl,
          );
          const cdnThumbnailUrls = (
            tile.char.portrait_cdn_thumbnail_urls
            || tile.char.portraitCdnThumbnailUrls
            || null
          );
          return (
            <button
              key={`${tile.kind}-${tile.char.character_id || tile.char.name}`}
              type="button"
              className={`campaign-slot-picker__tile${isPicked ? ' is-picked' : ''}`}
              onClick={() => handleTileClick(tile)}
              disabled={parentGeneratingBackstory}
              aria-pressed={isPicked}
              title={`${tile.char.name} — ${tile.char.race} ${tile.char.character_class}`}
              data-testid={`preset-character-card-${tileIndex}`}
            >
              {resolvedUrl ? (
                <ThumbnailImg
                  src={resolvedUrl}
                  size={128}
                  cdnThumbnailUrls={cdnThumbnailUrls}
                  alt={tile.char.name}
                  context="CampaignSlotEditor"
                  loading="lazy"
                />
              ) : (
                <span className="campaign-slot-picker__tile-fallback">
                  {(tile.char.name || '?').slice(0, 1).toUpperCase()}
                </span>
              )}
              <span className="campaign-slot-picker__tile-name">{tile.char.name}</span>
            </button>
          );
        })}
        {filteredTiles.length === 0 && (
          <p className="campaign-slot-picker__empty">No characters match.</p>
        )}
        {onCreateCharacter && (
          <button
            type="button"
            className="campaign-slot-picker__create-character"
            onClick={onCreateCharacter}
            disabled={parentGeneratingBackstory}
          >
            <span className="campaign-slot-picker__create-character-icon" aria-hidden="true">+</span>
            <span>Create Character</span>
          </button>
        )}
      </div>
    </div>
  );
});

CampaignSlotEditor.displayName = 'CampaignSlotEditor';

export default CampaignSlotEditor;
